/**
 * Small fixed-step 2D physics for cue/disc games (billiards, carrom).
 * The server runs the simulation; clients replay the returned frames.
 */

export interface Body {
  id: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  m: number;
  pocketed: boolean;
}

export interface Table {
  w: number;
  h: number;
  pockets: { x: number; y: number; r: number }[];
  friction: number; // linear deceleration, units/s^2
  cushion: number; // restitution on rails
  restitution: number; // ball-ball
}

export interface SimResult {
  frames: [number, number, number][][]; // per frame: [id, x, y] for live bodies
  firstContact: number | null; // id of first body touched by `cueId`
  pocketed: number[]; // ids in pocket order
  railAfterContact: boolean;
  collisions: number;
}

const DT = 1 / 240;
const MAX_STEPS = 240 * 20;
const STOP = 2; // speed below which a body stops

export function simulate(bodies: Body[], table: Table, cueId: number, frameEvery = 8): SimResult {
  const res: SimResult = { frames: [], firstContact: null, pocketed: [], railAfterContact: false, collisions: 0 };
  const snap = () =>
    res.frames.push(bodies.filter((b) => !b.pocketed).map((b) => [b.id, round(b.x), round(b.y)]));
  snap();
  for (let step = 0; step < MAX_STEPS; step++) {
    let moving = false;
    for (const b of bodies) {
      if (b.pocketed) continue;
      const sp = Math.hypot(b.vx, b.vy);
      if (sp < STOP) {
        b.vx = b.vy = 0;
        continue;
      }
      moving = true;
      const dec = Math.max(0, sp - table.friction * DT) / sp;
      b.vx *= dec;
      b.vy *= dec;
      b.x += b.vx * DT;
      b.y += b.vy * DT;
      // rails
      let hitRail = false;
      if (b.x < b.r) { b.x = b.r; b.vx = -b.vx * table.cushion; hitRail = true; }
      if (b.x > table.w - b.r) { b.x = table.w - b.r; b.vx = -b.vx * table.cushion; hitRail = true; }
      if (b.y < b.r) { b.y = b.r; b.vy = -b.vy * table.cushion; hitRail = true; }
      if (b.y > table.h - b.r) { b.y = table.h - b.r; b.vy = -b.vy * table.cushion; hitRail = true; }
      if (hitRail && res.firstContact !== null) res.railAfterContact = true;
      for (const p of table.pockets) {
        if (Math.hypot(b.x - p.x, b.y - p.y) < p.r) {
          b.pocketed = true;
          b.vx = b.vy = 0;
          res.pocketed.push(b.id);
          break;
        }
      }
    }
    // collisions
    for (let i = 0; i < bodies.length; i++) {
      const a = bodies[i];
      if (a.pocketed) continue;
      for (let j = i + 1; j < bodies.length; j++) {
        const b = bodies[j];
        if (b.pocketed) continue;
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const dist = Math.hypot(dx, dy);
        const minD = a.r + b.r;
        if (dist >= minD || dist === 0) continue;
        const nx = dx / dist;
        const ny = dy / dist;
        // separate
        const overlap = minD - dist;
        const tm = a.m + b.m;
        a.x -= nx * overlap * (b.m / tm);
        a.y -= ny * overlap * (b.m / tm);
        b.x += nx * overlap * (a.m / tm);
        b.y += ny * overlap * (a.m / tm);
        const rv = (b.vx - a.vx) * nx + (b.vy - a.vy) * ny;
        if (rv > 0) continue;
        const jImp = (-(1 + table.restitution) * rv) / (1 / a.m + 1 / b.m);
        a.vx -= (jImp / a.m) * nx;
        a.vy -= (jImp / a.m) * ny;
        b.vx += (jImp / b.m) * nx;
        b.vy += (jImp / b.m) * ny;
        res.collisions++;
        if (res.firstContact === null) {
          if (a.id === cueId) res.firstContact = b.id;
          else if (b.id === cueId) res.firstContact = a.id;
        }
      }
    }
    if (step % frameEvery === 0) snap();
    if (!moving) break;
  }
  for (const b of bodies) b.vx = b.vy = 0;
  snap();
  return res;
}

const round = (v: number) => Math.round(v * 10) / 10;

export function clampNum(v: unknown, lo: number, hi: number): number | null {
  if (typeof v !== "number" || !Number.isFinite(v)) return null;
  return Math.min(hi, Math.max(lo, v));
}
